import * as React from 'react'

import { currencyString, ENTITY_TYPES } from '@sales-center/common'
import { HeaderContainer } from './HeaderContainer'
import type { ILeadDetail } from '@user/store/lead/typings'
import type { IOpportunityDetail } from '@user/store/opportunity/typings'
import { LEAD_STATUSES, LeadStatus } from '../Leads/LeadCard/typings'
import {
  OPPORTUNITY_STATUSES,
  OppStatus,
} from '../Opportunities/OpportunityCard/typings'
import { TotalValuePopover } from '@user/components/EntityCards/atoms/TotalValuePopover'

import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { LocationsPopoverButton } from '@user/components/EntityCards/Locations'
import { Appointments } from '@user/components/Scheduling/Estimates'
import { useSelectFunnelStagesByType } from '@user/store/salesFunnel'
import { EditPencil } from '@user/components/DetailPages/sections/EntityName/Edit/EditPencil'
import { useSchedulingVisibility } from '@user/store/closingRequirements'
import { HeaderEntityWithCompanyStyled } from '@user/components/DetailPages/HeaderEntityWithCompany.styled'
import Stack from '@mui/material/Stack'
import Paper from '@mui/material/Paper'
import { useTheme } from '@mui/material/styles'

type BaseProps = {
  color?: string
  companyName: string
  disableEditName?: boolean
  entityName: string
  initialValue: number
  recurringValue: number
  totalValue: number
  canUpdate: boolean
  hasScheduledAppointment: boolean
}

type LeadProps = BaseProps & {
  detail: ILeadDetail
  entityStatus: LEAD_STATUSES
  entityType: ENTITY_TYPES.Lead
  mt?: number
}

type OpportunityProps = BaseProps & {
  detail: IOpportunityDetail
  entityStatus: OPPORTUNITY_STATUSES
  entityType: ENTITY_TYPES.Opportunity
  mt?: number
}

type Props = LeadProps | OpportunityProps

export function DetailPageHeader(props: Props) {
  const {
    color,
    detail,
    disableEditName,
    entityName,
    entityStatus,
    entityType,
    initialValue,
    recurringValue,
    totalValue,
    canUpdate,
    hasScheduledAppointment,
    mt = 2,
  } = props

  const stages = useSelectFunnelStagesByType(
    detail.salesFunnelId,
    entityType === ENTITY_TYPES.Lead ? 'Lead' : 'Opportunity',
  )

  const currentStage = React.useMemo(
    () => stages.filter((s) => s.id == detail.salesFunnelStageId).shift(),
    [detail.salesFunnelStageId, stages],
  )

  const status = React.useMemo((): LeadStatus | OppStatus => {
    if (detail.closedDate == null || !currentStage)
      return entityType === ENTITY_TYPES.Lead
        ? LEAD_STATUSES.Open
        : OPPORTUNITY_STATUSES.Open

    const { name } = currentStage

    switch (name) {
      case 'Disqualified':
        return LEAD_STATUSES.Disqualified
      case 'Qualify':
        return LEAD_STATUSES.Qualify
      case 'Closed/Won':
        return OPPORTUNITY_STATUSES.Won
      case 'Closed/Lost':
        return OPPORTUNITY_STATUSES.Lost
      default:
        return entityType === ENTITY_TYPES.Lead
          ? LEAD_STATUSES.Open
          : OPPORTUNITY_STATUSES.Open
    }
  }, [currentStage, detail.closedDate, entityType])

  const schedulingVisible = useSchedulingVisibility(detail.scheduleSettings)

  const showApptChip =
    ((entityType === ENTITY_TYPES.Lead &&
      status !== LEAD_STATUSES.Disqualified) ||
      (entityType === ENTITY_TYPES.Opportunity &&
        status === OPPORTUNITY_STATUSES.Open)) &&
    hasScheduledAppointment &&
    schedulingVisible
  const theme = useTheme()
  return (
    <Stack m={2} mt={mt}>
      <Paper elevation={0} variant="outlined">
        <HeaderContainer
          color={color || '##00ff00'}
          flexWrap="wrap"
          hideBorder
          paddingY="24px"
        >
          <Stack flexGrow={1}>
            <Stack direction="row" alignItems="center">
              <Box mr={1} width="46px" height="46px">
                {entityType === ENTITY_TYPES.Lead ? (
                  <img src="static-assets/icons/lead.svg" />
                ) : (
                  <img src="static-assets/icons/createOpportunity.svg" />
                )}
              </Box>

              <Box>
                <Typography
                  color={theme.palette.text.primary}
                  fontSize="14px"
                  mb={-0.5}
                >
                  {entityType === ENTITY_TYPES.Lead ? 'Lead' : 'Opportunity'}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <HeaderEntityWithCompanyStyled>
                    {entityName}
                  </HeaderEntityWithCompanyStyled>
                  {!disableEditName && <EditPencil {...props} />}
                </Stack>
              </Box>
            </Stack>

            <Stack direction="row" spacing={0} mt={0}>
              <LocationsPopoverButton
                buttonStyle="chip"
                entityId={detail.id}
                entityType={entityType}
                entityBranchId={detail.branchId}
                canUpdateEntity={canUpdate}
                matched={detail.externalLocationCode !== null}
                variant="entity"
              />
              {showApptChip && (
                <Box ml={1} mt={1.4}>
                  <Appointments entityId={detail.id} entityType={entityType} />
                </Box>
              )}
            </Stack>
          </Stack>

          <Box pl={3} pr={1}>
            <Typography color={theme.palette.text.primary}>
              Total Value
            </Typography>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography
                color={theme.palette.text.primary}
                fontSize="20px"
                fontFamily="Source Sans Pro Bold"
              >
                ${currencyString(totalValue)}
              </Typography>
              <Box display="flex" alignItems="center">
                <TotalValuePopover
                  entityStatus={entityStatus}
                  entityType={entityType}
                  entityDetail={detail}
                  initialValue={initialValue}
                  recurringValue={recurringValue}
                  totalValue={totalValue}
                />
              </Box>
            </Stack>
          </Box>
        </HeaderContainer>
      </Paper>
    </Stack>
  )
}
